<template>

    <div class="order-add-item-form">

        <div class="w-full pt-4">
            <h3 class="text-base font-medium leading-6 text-gray-900">Add product to your order</h3>

            <p class="text-sm text-gray-600">
                Search for and select product(s) to be added to your order.
            </p>
        </div>

        <form @submit.prevent="addProductToOrder" class="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3 pt-6" v-if="loaded">

            <ProductSearchSelector :data="products.products.data" v-show="product_display" @closeModal="toggleProductDisplay" @selectProduct="selectProduct"></ProductSearchSelector>

            <div class="form-control col-span-2" :class="{ 'form-control-error': value.fieldHasErrors('sku') }">
                <input type="text" name="order_sku" id="order_sku" placeholder="Search or Select a Product" autocomplete="off" v-model="product_search_query" aria-label="Search or Select a Product" aria-required="true" @click.prevent="toggleProductDisplay" @keyup="productSearchTrigger">
                <span class="pb-2" v-if="value.fieldHasErrors('sku')">{{ value.getFieldError('sku') }}</span>
            </div>
            <div class="w-full justify-start" :class="{ 'col-span-2': ! selected_product.pan || ! selected_product.serial }" v-if="selected_product">
                <div class="" v-if="selected_product">
                    <img :src="selected_product.meta.image" :alt="selected_product.name" class="bg-contain h-16 m-auto flex shadow">
                </div>

                <h4 class="font-medium text-sm mt-2 text-center">{{ selected_product.name }}</h4>

                <p class="text-sm text-gray-600" >
                    {{ ellipsis(selected_product.meta.description, 20) }}
                </p>
            </div>
            <div class="w-full justify-start flex pt-2 col-span-2 text-gray-600 text-sm" v-if="!selected_product">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 mr-2 hidden md:flex" fill="none" viewBox="0 0 24 24" stroke="currentColor" focusable="false" role="img" aria-label="Please Select a Product First">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
                <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 mr-2 flex md:hidden" fill="none" viewBox="0 0 24 24" stroke="currentColor" focusable="false" role="img" aria-label="Please Select a Product First">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 10l7-7m0 0l7 7m-7-7v18" />
                </svg>
                No product selected, select a product first.
            </div>
            <div class="form-control-group" :class="{ 'form-control-error': value.fieldHasErrors('value') }">
                <div class="input-group">
                    <span class="w-8" :class="{ 'disabled': !selected_product }">{{ selected_product ? currencySymbol(selected_product.currency) : ' ' }}</span>
                    <input type="text" name="order_value" id="order_value" autocomplete="off" placeholder="Value" class="w-full border-l-0" required v-model="selected_attributes.value" aria-label="Value" aria-required="true" :disabled="!selected_product">
                </div>
                <span class="pb-2" v-if="value.fieldHasErrors('value')">{{ value.getFieldError('value') }}</span>
            </div>
            <div class="form-control" :class="{ 'form-control-error': value.fieldHasErrors('quantity') }" v-if="selected_product.type !== 'reload'">
                <input type="text" name="order_quantity" id="order_quantity"  placeholder="Quantity" v-model="selected_attributes.quantity" autocomplete="off" required aria-label="Quantity" aria-required="true" :disabled="!selected_product">
                <span class="pb-2" v-if="value.fieldHasErrors('quantity')">{{ value.getFieldError('quantity') }}</span>
            </div>
            <div class="form-control" :class="{ 'form-control-error': value.fieldHasErrors('pan') }" v-if="selected_product.pan">
                <input type="text" name="pan" id="pan" maxlength="32" placeholder="PAN" v-model="selected_attributes.pan" autocomplete="off" required aria-label="PAN" aria-required="true" :disabled="!selected_product">
                <span class="pb-2" v-if="value.fieldHasErrors('pan')">{{ value.getFieldError('pan') }}</span>
            </div>
            <div class="form-control" :class="{ 'form-control-error': value.fieldHasErrors('serial') }" v-if="selected_product.serial">
                <input type="text" name="serial" id="serial" maxlength="32" placeholder="Serial number" v-model="selected_attributes.serial" autocomplete="off" required aria-label="Serial number" aria-required="true" :disabled="!selected_product">
                <span class="pb-2" v-if="value.fieldHasErrors('serial')">{{ value.getFieldError('serial') }}</span>
            </div>
            <div class="col-span-2 lg:col-span-1 justify-center">

                <button id="add-order-item" type="submit" class="btn w-full text-sm justify-center items-center" :disabled="!selected_product || value.isFormUpdating()">
                    <svg class="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" v-if="value.isFormUpdating()">
                        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Add Item
                </button>
            </div>

        </form>
        <Loading :message="false" v-else></Loading>
    </div>


</template>

<script>
import Fuse from "fuse.js";
import Loading from "../Support/Loading";
import ProductSearchSelector from "../Product/ProductSearchSelector";
import {FormattingMixin, PermissionMixin} from "../../support/mixins";

export default {
    name: "OrderAddItemForm",
    mixins: [PermissionMixin, FormattingMixin],
    props: {
        value: {
            type: Object,
            required: true
        },
        products: {
            type: Object,
            required: true
        }
    },
    data() {
        return {
            selected_product: false,
            selected_attributes: {
                value: null,
                quantity: null,
                pan: null,
                serial: null,
            },
            searchResults: [],
            searchTrigger: null,
            product_search_query: null,
            product_display: false,
            loaded: false
        }
    },
    components: {
        ProductSearchSelector,
        Loading
    },
    methods: {
        selectProduct(product) {
            this.selected_product = product;
            this.product_search_query = this.selected_product.name;

            // hide product selection
            this.product_display = false;
        },
        addProductToOrder() {
            let vm = this;

            vm.value.addProductToOrder(vm.selected_product, vm.selected_attributes).then(function () {
                // reset product selection for currency selection
                if (vm.value.data.length === 0) {
                    vm.products.products = false;
                }

                vm.resetSelectedProduct();
            }).catch(error => vm.handleError(error));
        },
        productSearchTrigger() {
            let vm = this;

            if (vm.searchTrigger) {
                clearTimeout(vm.searchTrigger);
                vm.searchTrigger = null;
            }

            vm.searchTrigger = setTimeout(function () {
                const options = {
                    shouldSort: true,
                    includeScore: true,
                    threshold: 0.4,
                    keys: ['name']
                }

                let fuse = new Fuse(vm.products.products.data, options);

                let results = fuse.search(vm.product_search_query);

                vm.searchResults = results.map(r => r.item);
            }, 500)
        },
        toggleProductDisplay() {
            let vm = this;

            vm.product_display = ! vm.product_display;
        },
        resetSelectedProduct() {
            let vm = this;

            return new Promise(resolve => {
                vm.selected_attributes = {
                    value: null,
                    quantity: null,
                    pan: null,
                    serial: null,
                };
                vm.product_search_query = null;
                vm.selected_product = false;

                resolve(true);
            });
        }
    },
    mounted() {
        let vm = this;

        new Promise(resolve => {
            vm.resetSelectedProduct().then(function () {
                resolve(true);
            });
        }).then(result => {
            vm.loaded = result;
        });
    }
}
</script>
